import { useReducer, useCallback, useEffect, useMemo, useState } from 'react';

export { default as usePrevious } from './usePrevious';
export { default as useCustomErrorCode, CUSTOM_ERROR_CODE } from './useCustomErrorCode';
export { default as useAccountLockout } from './useAccountLockout';
export { default as useQueryParams } from './useQueryParams';
export { default as useBounceUrl } from './useBounceUrl';
export { default as useFeatureFlags } from './useFeatureFlags';
export { default as usePaginationControls } from './usePaginationControls';
export { default as useDebounce } from './useDebounce';
export { default as useBaseInfiniteQuery } from './useBaseInfiniteQuery';
export { default as useInfiniteScroll } from './useInfiniteScroll';
export { default as useAnalytics } from './analytics';

export const useAsync = (fn, runImmediately = true) => {
  const [{ data, error, status }, dispatch] = useReducer(
    (state, action) => ({ ...state, ...action }),
    {
      data: null,
      error: null,
      status: null,
    }
  );

  const setData = useCallback(value => dispatch({ data: value, status: 'resolved' }), [dispatch]);

  const setError = useCallback(err => dispatch({ error: err, status: 'rejected' }), [dispatch]);

  const run = useCallback(
    promise => {
      dispatch({
        data: null,
        error: null,
        status: 'pending',
      });

      return Promise.resolve(promise).then(result => {
        setData(result);
        return result;
      }, setError);
    },
    [dispatch, setData, setError]
  );

  const execute = useCallback((...args) => run(fn(...args)), [run, fn]);

  useEffect(() => {
    if (runImmediately && status === null) {
      execute();
    }
  }, [execute, runImmediately, status]);

  return [
    {
      data,
      error,
      status,

      isLoading: status === 'pending',
      isError: status === 'rejected',
      isSuccess: status === 'resolved',
    },
    execute,
  ];
};

export const useLocalStorage = (keyName, defaultState) => {
  const initialState = useMemo(() => {
    const value = JSON.parse(window.localStorage.getItem(keyName));

    return {
      ...defaultState,
      ...value,
    };
  }, [keyName, defaultState]);

  const [value, setValue] = useState(initialState);

  useEffect(() => {
    const serializedValue = JSON.stringify(value);
    window.localStorage.setItem(keyName, serializedValue);
  }, [value, keyName]);

  return [value, setValue];
};
